import axios from "axios";
import { API_BASE_URL, API_URL, NODE_API_BASE_URL } from "../../config";

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("cms_token")}`,
});

// ==================== USER PROFILE SERVICES ====================
/**
 * Fetch user lists
 */
export const fetchUserList = async (page = 1, filterData = {}) => {
  try {
    const res = await axios.get(`${NODE_API_BASE_URL}/api/subscription/get-customer-list-v3`, {
      params: { page, limit: 10, ...filterData },
      headers: getAuthHeaders(),
    });

    const payload = res?.data;

    return {
      data: Array.isArray(payload?.data) ? payload.data : [],
      total: Number(payload?.total ?? 0),
      lastPage: payload?.last_page ?? 1,
    };
  } catch (err) {
    throw new Error(err?.response?.data?.message || err?.message || "Failed to fetch user list");
  }
};

 

/**
 * Fetch user details
 */
export const fetchUserDetails = async (userId) => {
  const response = await axios.get(`${API_BASE_URL}/get-user-details`, {
    params: { user_id: userId },
    headers: getAuthHeaders(),
  });

  if (response.data.status === 200) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to fetch user details");
};

/**
 * Fetch user subscription details
 * Returns { user, subscription_plan, attorney_numbers, subscription_logs }
 */
export const fetchSubscriptionDetails = async (userId) => {
  const response = await axios.get(`${API_BASE_URL}/subscription/get-subscription-details`, {
    params: { user_id: userId },
    headers: getAuthHeaders(),
  });

  if (response.data.success === true) {
    return response.data.data;
  }
  throw new Error(
    response.data.message || "Failed to fetch subscription details",
  );
};

/**
 * Fetch attorney numbers and TM counts
 */
export const fetchAttorneyDetails = async (userId) => {
  const response = await axios.get(`${API_BASE_URL}/get-attorney-details`, {
    params: { user_id: userId },
    headers: getAuthHeaders(),
  });

  if (response.data.status === 200) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to fetch attorney details");
};

/**
 * Fetch features
 */
export const fetchFeatures = async () => {
  const response = await axios.get(`${API_BASE_URL}/get-features`, {
    headers: getAuthHeaders(),
  });
  
  
  if (response.status === 200) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to fetch plan features");
};

/**
 * Fetch API calls history with pagination
 */
export const fetchApiCalls = async ({ userId, page = 1, limit = 10 }) => {
  const response = await axios.get(`${API_BASE_URL}/get-user-api-calls`, {
    params: { user_id: userId, page, limit },
    headers: getAuthHeaders(),
  });

  if (response.data.status === 200) {
    return {
      data: response.data.data,
      total: response.data.total_data,
      currentPage: page,
      totalPages: Math.ceil(response.data.total_data / limit),
    };
  }
  throw new Error(response.data.message || "Failed to fetch API calls");
};

/**
 * Fetch wallet transactions with pagination
 */
export const fetchWalletTransactions = async ({
  userId,
  page = 1,
  limit = 10,
}) => {
  const response = await axios.get(`${API_BASE_URL}/get-wallet-details`, {
    params: { user_id: userId, page, limit },
    headers: getAuthHeaders(),
  });

  if (response.data.status === 200) {
    return {
      data: response.data.details,
      total: response.data.total,
      balance: response.data.balance,
      currentPage: page,
      totalPages: Math.ceil(response.data.total / limit),
    };
  }
  throw new Error(
    response.data.message || "Failed to fetch wallet transactions",
  );
};

// ==================== UPDATE SERVICES ====================

/**
 * Update user details
 */
export const updateUserDetails = async ({ userId, data }) => {
  const response = await axios.post(
    `${API_BASE_URL}/update-user-details`,
    { user_id: userId, ...data },
    { headers: getAuthHeaders() },
  );

  if (response.data.success !== true) {
    throw new Error(response.data.message || "Failed to update user details");
  }

  return response.data;
};

/**
 * Update subscription plan
 */
export const updateSubscriptionPlan = async ({ userId, planData }) => {
  planData.append("user_id", userId);

  const response = await axios.post(
    `${API_BASE_URL}/subscription/update-user-subscription`,
    planData,
    { headers: getAuthHeaders() },
  );

  if (response.data.success === true || response.data.pending_approval === true || response.data.online_payment === true) {
    return response.data;
  }
  throw new Error(response.data.message || "Failed to update subscription");
};



/**
 * Save/Update updation requirement
 */
export const saveUpdationRequirement = async ({ userId, formData }) => {
  formData.append("user_id", userId);

  const response = await axios.put(
    `${API_BASE_URL}/update-updation-requirement`,
    formData,
    { headers: getAuthHeaders() }
  );

  if (response.data.status === 200) {
    return response.data.data;
  }
  throw new Error(
    response.data.message || "Failed to save updation requirement"
  );
};



/**
 * Fetch subscription history for a user
 */
export const fetchSubscriptionHistory = async (userId) => {
  const response = await axios.get(
    `${API_BASE_URL}/get-subscription-history`,
    {
      params: { user_id: userId },
      headers: getAuthHeaders(),
    }
  );

  if (response.data.status === 200) {
    return response.data.data;
  }
  throw new Error(
    response.data.message || "Failed to fetch subscription history"
  );
};


/**
 * Update plan features
 */
export const updatePlanFeatures = async ({ userId, features }) => {
  const response = await axios.put(
    `${API_BASE_URL}/update-plan-features`,
    { user_id: userId, features },
    { headers: getAuthHeaders() },
  );

  if (response.data.status === 200) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to update features");
};

/**
 * Add new attorney numbers
 * Payload: { user_id, attorney_numbers: [{ number, tm_count }] }
 */
export const addAttorneyNumbers = async ({ userId, attorneyNumbers }) => {
  const response = await axios.post(
    `${API_BASE_URL}/subscription/add-attorney-numbers`,
    { user_id: userId, attorney_numbers: attorneyNumbers },
    { headers: getAuthHeaders() },
  );

  if (response.data.status === 200 || response.data.success) {
    return response.data;
  }
  throw new Error(response.data.message || "Failed to add attorney numbers");
};

/**
 * Update attorney numbers
 */
export const updateAttorneyNumbers = async ({ userId, attorneyNumbers }) => {
  const response = await axios.put(
    `${API_BASE_URL}/update-attorney-numbers`,
    { user_id: userId, attorney_numbers: attorneyNumbers },
    { headers: getAuthHeaders() },
  );

  if (response.data.status === 200) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to update attorney numbers");
};


/**
 * Update user status (activate/deactivate)
 */
export const updateUserStatus = async ({ userId, status }) => {
  const response = await axios.post(
    `${API_BASE_URL}/manage-user-status?status=${status}&user_id=${userId}`,
    { user_id: String(userId), status },
    { headers: getAuthHeaders() },
  );

  if (response.data.status === 200) {
    return response.data.data;
  }
  throw new Error(response.data.message || "Failed to update status");
};
